import type { GatingStrategy } from "./gating-strategy";
import { GatingStrategyType } from "./gating-strategy";
import type { ServiceDefinition } from "@/composables/use-lazy-services";
import type { Ref } from "vue";
import type { WebThreeTokenWithBalance } from "@/constants/web-three";

/**
 * Dependencies passed to strategy factories.
 */
export interface GatingStrategyDeps {
  tokenBalanceMap: Ref<Map<string, WebThreeTokenWithBalance>>;
}

/**
 * Config derived from ChannelCapabilities.
 * Each key maps to a gating strategy type.
 */
export interface GatingStrategyConfig {
  [GatingStrategyType.membership]: boolean;
  [GatingStrategyType.token]: boolean;
  [GatingStrategyType.points]: boolean;
  [GatingStrategyType.product]: boolean;
}

/**
 * Registry mapping capabilities to their strategy loaders.
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 * - Which capabilities enable which strategies
 * - How to dynamically import each strategy
 *
 * To add a new gating type:
 * 1. Add the capability flag to GatingStrategyConfig
 * 2. Add the loader to this registry
 * 3. Create the strategy file
 */
export const GATING_STRATEGY_REGISTRY: {
  [K in keyof GatingStrategyConfig]: ServiceDefinition<GatingStrategy>;
} = {
  [GatingStrategyType.membership]: {
    load: () =>
      import("./membership-gating.strategy").then((m) => ({
        create: () => m.createMembershipGatingStrategy(),
      })),
  },

  [GatingStrategyType.token]: {
    load: () =>
      import("./token-gating.strategy").then((m) => ({
        create: () => m.createTokenGatingStrategy(),
      })),
  },

  [GatingStrategyType.points]: {
    load: () =>
      import("./points-gating.strategy").then((m) => ({
        create: () => m.createPointsGatingStrategy(),
      })),
  },

  [GatingStrategyType.product]: {
    load: () =>
      import("./product-purchase-gating.strategy").then((m) => ({
        create: () => m.createProductPurchaseGatingStrategy(),
      })),
  },
};
